/** Decorative line icons (aria-hidden). currentColor only: no colour literals. One path per glyph, 24-unit grid, 1.75 stroke. */
import type { IconName } from './nav';

export type GlyphName = IconName | 'menu' | 'close' | 'search' | 'globe' | 'moon' | 'sun' | 'logout' | 'collapse' | 'expand' | 'chevron' | 'chevron-end' | 'arrow-end' | 'more' | 'sparkle' | 'file' | 'user-plus' | 'eye' | 'key' | 'link' | 'check-circle' | 'clock' | 'wave' | 'headset' | 'rocket';

const PATHS: Record<GlyphName, string> = {
  home: 'M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  dashboard: 'M4 4h6v7H4zM14 4h6v4h-6zM14 11h6v9h-6zM4 14h6v6H4z',
  audit: 'M3 17l5-6 4 4 5-7 4 4M3 21h18',
  seo: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-5-5M8 11h6M11 8v6',
  leads: 'M16 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM4 21a8 8 0 0 1 16 0',
  crm: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM17 11a3 3 0 1 0 0-6M3 20a6 6 0 0 1 12 0M15 14a6 6 0 0 1 6 6',
  campaigns: 'M3 11v2l13 5V6zM16 9a3 3 0 0 1 0 6M7 13v5a1 1 0 0 0 1 1h1',
  inbox: 'M4 5h16v11H8l-4 4z',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  approvals: 'M5 12l4 4L19 6',
  deliverables: 'M4 4h16v16H4zM8 12l3 3 5-6',
  reports: 'M5 20V12M10 20V6M15 20v-9M20 20V3',
  integrations: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 17h6M17 14v6',
  billing: 'M3 6h18v12H3zM3 10h18M7 15h4',
  ads: 'M3 11v2l13 5V6zM16 9a3 3 0 0 1 0 6',
  content: 'M4 4h16v16H4zM4 15l5-5 4 4 3-3 4 4',
  branding: 'M12 3a9 9 0 1 0 0 18c1 0 2-1 2-2s-1-2 0-3h3a4 4 0 0 0 4-4 9 9 0 0 0-9-9z',
  team: 'M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM17 11a3 3 0 1 0 0-6M3 20a6 6 0 0 1 12 0M15 14a6 6 0 0 1 6 6',
  organization: 'M4 21V5l8-3 8 3v16M9 9h2M13 9h2M9 13h2M13 13h2M10 21v-4h4v4',
  whitelabel: 'M3 12l9-9h9v9l-9 9zM16 8h.01',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .9-1 1.7M12 17h.01',
  settings: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM4 12h2M18 12h2M12 4v2M12 18v2',
  pin: 'M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11zM12 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  shield: 'M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6z',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 20a2 2 0 0 0 4 0',
  menu: 'M4 6h16M4 12h16M4 18h16',
  close: 'M6 6l12 12M18 6L6 18',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM21 21l-5-5',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18',
  moon: 'M20 14A8 8 0 0 1 10 4a8 8 0 1 0 10 10z',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v3M12 20v3M4 12H1M23 12h-3',
  logout: 'M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10',
  collapse: 'M15 6l-6 6 6 6M20 4v16',
  expand: 'M9 6l6 6-6 6M4 4v16',
  chevron: 'M6 9l6 6 6-6',
  'chevron-end': 'M9 6l6 6-6 6',
  'arrow-end': 'M5 12h14M13 6l6 6-6 6',
  more: 'M12 6h.01M12 12h.01M12 18h.01',
  sparkle: 'M12 3v4M12 17v4M3 12h4M17 12h4M6.5 6.5l2 2M15.5 15.5l2 2M6.5 17.5l2-2M15.5 8.5l2-2',
  file: 'M6 3h8l4 4v14H6zM14 3v4h4M9 13h6M9 17h6',
  'user-plus': 'M14 11a4 4 0 1 0-8 0 4 4 0 0 0 8 0zM2 21a8 8 0 0 1 16 0M19 8v6M16 11h6',
  eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  key: 'M15 3a6 6 0 1 0 0 12 6 6 0 0 0 0-12zM10.5 13.5L3 21M6 18l2 2M8 16l2 2',
  link: 'M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1',
  'check-circle': 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8 12l3 3 5-6',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2',
  wave: 'M3 14c2 0 2-3 4-3s2 3 4 3 2-3 4-3 2 3 4 3M3 19c2 0 2-3 4-3s2 3 4 3 2-3 4-3 2 3 4 3M3 9c2 0 2-3 4-3s2 3 4 3 2-3 4-3 2 3 4 3',
  headset: 'M4 13a8 8 0 0 1 16 0M4 13v4a2 2 0 0 0 2 2h2v-6H4zM20 13v4a2 2 0 0 1-2 2h-2v-6h4M14 21h-2',
  rocket: 'M12 3c3 2 5 6 5 10l-2 3h-6l-2-3c0-4 2-8 5-10zM9 16l-3 5M15 16l3 5M12 10h.01',
};

export function Icon({ name, size = 20 }: { name: GlyphName; size?: number }) {
  return (
    <svg className="qm-icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d={PATHS[name]} />
    </svg>
  );
}
